import { UserFirestoreService } from 'src/app/database/firestore/user-firestore.service';
import { Injectable } from '@angular/core';
import { User as UserModel } from './user.model';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map, distinctUntilChanged, tap } from 'rxjs/operators';
import { UserStoreService } from 'src/app/database/store/user-store.service';
import { Timestamp } from '@angular/fire/firestore';
import { User } from '@angular/fire/auth';
import { AuthService } from '../auth/auth.service';


@Injectable({
  providedIn: 'root',
})
export class UserService {
  // Documentação da implementação do user:
  // https://github.com/angular/angularfire/blob/57d9005d2e0077d3920e66b5cbcaea1fb7f84450/docs/firestore/collections.md

  private keyboardAppearanceCache = {};
  private impersonatedUserUid: string | null = null;
  private userSubscription: Subscription;

  private isImpersonatingSubject = new BehaviorSubject<boolean>(false);
  isImpersonating$ = this.isImpersonatingSubject.asObservable();

  displayName: string;


  constructor(
    private userStore: UserStoreService,
    private userFirestore: UserFirestoreService,
    private authService: AuthService,
  ) {
    this.userStore.state$.subscribe((user) => {
      this.keyboardAppearanceCache = {};
      if (this.userStore.state.appearanceBackgroundColor) {
        this.keyboardAppearanceCache[`${user.appearanceBackgroundColor}`] =
          true;
      }
      if (this.userStore.state.appearanceFontColor) {
        this.keyboardAppearanceCache[`${user.appearanceFontColor}`] = true;
      }
      if (this.userStore.state.appearanceFontBold) {
        this.keyboardAppearanceCache[`keyboard-letter-bold`] = true;
        document.documentElement.style.setProperty('--user-bold', 'bold');
      } else {
        document.documentElement.style.setProperty('--user-bold', 'light');
      }
      if (this.userStore.state.appearanceFontBig) {
        this.keyboardAppearanceCache[`keyboard-letter-big`] = true;
      } 
    });

    this.authService.user$.subscribe((authUser) => {
      if (this.userSubscription) {
        this.userSubscription.unsubscribe();
      }
      if (authUser && !this.impersonatedUserUid) {
        this.loadUser(authUser.uid);
        this.displayName = authUser.displayName;
      } else if (!authUser) {
        this.clear('auth user logged out');
      }
    });
  }

  private loadUser(uid: string) {
    // Subscribe to user document
    this.userSubscription = this.userFirestore
      .doc$(uid)
      .pipe(
        tap((userDoc) => {
          if (userDoc) {
            this.userStore.patch(userDoc, `user doc subscription`);
            const authUser = this.authService.userAuth;
          }
        })
      )
      .subscribe();
  }

  impersonateUser(uid: string) {
    this.impersonatedUserUid = uid;
    this.isImpersonatingSubject.next(true);
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    // Clear previous user data
    this.userStore.clear(`load user ${uid}`);
    this.loadUser(uid);
  }

  stopImpersonation() {
    this.impersonatedUserUid = null;
    this.isImpersonatingSubject.next(false);
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    const authUser = this.authService.userAuth;
    if (authUser) {
      this.loadUser(authUser.uid);
    } else {
      this.clear('impersonation stopped, no auth user');
    }
  }

  get name$() {
    return this.userStore.state$.pipe(map((state) => state.name));
  }

  // get displayName$() {
  //   return this.userStore.state$.pipe(
  //     map((state) => state.displayName || state.name)
  //   );
  // }

  get email$() {
    return this.userStore.state$.pipe(map((state) => state.email));
  }

  get uid$() {
    return this.userStore.state$
      .pipe(map((state) => state.uid))
      .pipe(distinctUntilChanged());
  }

  get credentials$() {
    return this.userStore.state$.pipe(map((state) => state.credentials));
  }

  get activeProfile$() {
    return this.userStore.state$.pipe(map((state) => state.activeProfile));
  }

  get claims$() {
    return this.authService.claims$;
  }

  get uid() {
    return this.userStore.state.uid;
  }
  get admin() {
    return this.authService.isAdmin();
  }
  get analyst() {
    return this.authService.claims?.analyst;
  }
  get support() {
    return this.authService.isSupport();
  }
  get userData() {
    return this.userStore.state;
  }

  get userData$() {
    return this.userStore.state$;
  }

  get activeProfile() {
    return this.userStore.state.activeProfile;
  }

  set activeProfile(value: string) {
    this.userStore.patch({ activeProfile: value }, 'user set active profile');
  }

  get customVoices() {
    return this.userStore.state.customVoices;
  }

  get claims() {
    return this.authService.claims;
  }

  get appearanceFontColor() {
    return this.userStore.state.appearanceFontColor || "keyboard-letter-color-standard";
  }

  get appearanceFontColor$() {
    return this.userStore.state$.pipe(
      map((state) => state.appearanceFontColor)
    );
  }

  set appearanceFontColor(value: string) {
    this.userStore.patch(
      { appearanceFontColor: value },
      'user set appearanceFontColor'
    );
    this.commit();
  }

  get appearanceBackgroundColor() {
    return this.userStore.state.appearanceBackgroundColor || "keyboard-contrast-white";
  }

  get appearanceBackgroundColor$() {
    return this.userStore.state$.pipe(
      map((state) => state.appearanceBackgroundColor)
    );
  }

  set appearanceBackgroundColor(value: string) {
    this.userStore.patch(
      { appearanceBackgroundColor: value },
      'user set appearanceBackgroundColor'
    );
    this.commit();
  }

  get appearanceFontBold() {
    return this.userStore.state.appearanceFontBold;
  }

  set appearanceFontBold(value: boolean) {
    this.userStore.patch(
      { appearanceFontBold: value },
      'user set appearanceFontBold'
    );
    this.commit();
  }

  get appearanceFontBig() {
    return this.userStore.state.appearanceFontBig;
  }

  set appearanceFontBig(value: boolean) {
    this.userStore.patch(
      { appearanceFontBig: value },
      'user set appearanceFontBig'
    );
    this.commit();
  }

  get simpleNavigation() {
    return this.userStore.state.simpleNavigation;
  }

  set simpleNavigation(value: boolean) {
    this.userStore.patch(
      { simpleNavigation: value },
      'user set simpleNavigation'
    );
    this.commit();
  }

  get simpleNavigation$() {
    return this.userStore.state$.pipe(map((state) => state.simpleNavigation));
  }

  get reducedNavigation() {
    return this.userStore.state.reducedNavigation;
  }

  set reducedNavigation(value: boolean) {
    this.userStore.patch(
      { reducedNavigation: value },
      'user set reducedNavigation'
    );
    this.commit();
  }

  get reducedNavigation$() {
    return this.userStore.state$.pipe(map((state) => state.reducedNavigation));
  }

  get keyboardAppearance() {
    return this.keyboardAppearanceCache;
  }

  get appearanceCursorBorderColor() {
    return this.userStore.state.appearanceCursorBorderColor || "cursor-border-color-blue";
  }

  get appearanceCursorBorderColor$() {
    return this.userStore.state$.pipe(
      map((state) => state.appearanceCursorBorderColor)
    );
  }

  set appearanceCursorBorderColor(value: string) {
    this.userStore.patch(
      { appearanceCursorBorderColor: value },
      'user set appearanceCursorBorderColor'
    );
    console.log('appearanceCursorBorderColor', value);
    this.commit();
  }

  get gridSizeAac() {
    return this.userStore.state.gridSize?.[0] ?? null;
  }

  get gridSizeAac$() {
    return this.userStore.state$.pipe(map((state) => state.gridSize[0]));
  }

  set gridSizeAac(value: string) {
    this.userStore.patch(
      { gridSize: [value, this.gridSizeActivities] },
      'user set gridSizeAac'
    );
    this.commit();
  }

  get gridSizeActivities() {
    return this.userStore.state.gridSize?.[1] ?? null;
  }

  get gridSizeActivities$() {
    return this.userStore.state$.pipe(map((state) => state.gridSize[1]));
  }

  set gridSizeActivities(value: string) {
    this.userStore.patch(
      { gridSize: [this.gridSizeAac, value] },
      'user set gridSizeActivities'
    );
    this.commit();
  }

  get availableResources() {
    return this.userStore.state.availableResources;
  }

  get availableResources$() {
    return this.userStore.state$.pipe(map((state) => state.availableResources));
  }

  set availableResources(value: any) {
    this.userStore.patch(
      { availableResources: value },
      'user set availableResources'
    );
    this.commit();
  }

  async create(user: UserModel) {
    // Initialize before creation
    this.userStore.clear('clear before creation');

    user.activeProfile = 'default';

    return this.userFirestore
      .create(user, user.uid)
      .then(() => {
        // this.userStore.patch({
        //   creationTimestamp: Timestamp.now()
        // }, 'user create SUCCESS');
        // setTimeout(() => this.userStore.patch({ formStatus: '' }, 'employee create timeout reset formStatus'), 2000);
      })
      .catch((err) => {
        // this.userStore.patch({ loading: false, formStatus: 'An error ocurred' }, 'user create ERROR' + err);
        console.error('user create ERROR', err);
      });
  }

  async updateUser(uid: string, data: Partial<UserModel>) {
    try {
      await this.userFirestore.update(data, uid);
      console.log(`User ${uid} updated successfully with data:`, data);
    } catch (error) {
      console.error(`Failed to update user ${uid}:`, error);
      throw error;
    }
  }

  async clear(event: string = undefined) {
    // Initialize before creaation
    this.userStore.clear(event);
  }

  setName(value: string, commit: boolean = false) {
    this.userStore.patch(
      {
        name: value,
      },
      'user set name'
    );
    if (commit) {
      this.userFirestore.update({ name: value }, this.userStore.state.uid);
    } else {
      return Promise.resolve();
    }
  }

  setEmail(value: string, commit: boolean = false) {
    this.userStore.patch(
      {
        name: value,
      },
      'user set email'
    );
    if (commit) {
      return this.userFirestore.update(
        { email: value },
        this.userStore.state.uid
      );
    } else {
      return Promise.resolve();
    }
  }

  setActiveProfile(value: string, commit: boolean = false) {
    this.userStore.patch(
      {
        activeProfile: value,
      },
      'user set active profile'
    );
    if (commit) {
      return this.userFirestore.update(
        { activeProfile: value },
        this.userStore.state.uid
      );
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Sends all user data (avoid usage, since  it may overwrite)
   */
  commit() {
    return this.userFirestore.update(
      this.userStore.state,
      this.userStore.state.uid
    );
  }

  // initWithAuth(uid: string) {
  //   this.userDoc = this.afs.doc<User>('users/' + uid);
  // }

  // private update(user: User) {
  //   return this.userDoc.update(user);
  // }

  isAdmin() {
    return this.authService.isAdmin();
  }

  isSupport() {
    return this.authService.isSupport();
  }
}
